import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState, useEffect, useRef } from 'react';
import api from '../lib/api';
import { useAuthStore } from '../stores/auth.store';
import { useWorkspaceStore } from '../stores/workspace.store';
import { TermsModal } from '../components/TermsModal';
import { InteractiveBackground } from '../components/InteractiveBackground';
import { LoginSlideshow } from '../components/LoginSlideshow';
import { APP_VERSION } from '../version';

export const Route = createFileRoute('/login')({
  component: LoginPage,
});

// Enum definido localmente para evitar problemas de importação ESM/CommonJS
enum ProfessionalProfile {
  SOCIO = 'SOCIO',
  ADVOGADO_ASSOCIADO = 'ADVOGADO_ASSOCIADO',
  ADVOGADO_CELETISTA = 'ADVOGADO_CELETISTA',
  ADVOGADO_AUTONOMO = 'ADVOGADO_AUTONOMO',
  BACHAREL = 'BACHAREL',
  ESTAGIARIO = 'ESTAGIARIO',
  TRAINEE = 'TRAINEE',
  ADMINISTRATIVO = 'ADMINISTRATIVO',
  FINANCEIRO = 'FINANCEIRO',
}

const BRAZILIAN_STATES = [
  'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA',
  'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN',
  'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'
];

const PROFESSIONAL_PROFILES = [
  { value: ProfessionalProfile.SOCIO, label: 'Sócio' },
  { value: ProfessionalProfile.ADVOGADO_ASSOCIADO, label: 'Advogado Associado' },
  { value: ProfessionalProfile.ADVOGADO_CELETISTA, label: 'Advogado Celetista' },
  { value: ProfessionalProfile.ADVOGADO_AUTONOMO, label: 'Advogado Autônomo' },
  { value: ProfessionalProfile.BACHAREL, label: 'Bacharel' },
  { value: ProfessionalProfile.ESTAGIARIO, label: 'Estagiário' },
  { value: ProfessionalProfile.TRAINEE, label: 'Trainee' },
  { value: ProfessionalProfile.ADMINISTRATIVO, label: 'Administrativo' },
  { value: ProfessionalProfile.FINANCEIRO, label: 'Financeiro' },
];

const TERMS_VERSION = '1.0.0';

function LoginPage() {
  const navigate = useNavigate();
  const setAuth = useAuthStore((state) => state.setAuth);
  const logout = useAuthStore((state) => state.logout);
  const user = useAuthStore((state) => state.user);
  const setWorkspace = useWorkspaceStore((state) => state.setWorkspace);
  
  // Tab state
  const [activeTab, setActiveTab] = useState<'login' | 'register'>('login');
  
  // Login state
  const [email, setEmail] = useState(localStorage.getItem('rememberedEmail') || '');
  const [password, setPassword] = useState(localStorage.getItem('rememberedPassword') || '');
  const [rememberMe, setRememberMe] = useState(!!(localStorage.getItem('rememberedEmail') && localStorage.getItem('rememberedPassword')));
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState('');
  
  // Register state
  const [registerForm, setRegisterForm] = useState({
    fullName: '',
    email: '',
    password: '',
    confirmPassword: '',
    address: '',
    professionalProfile: '' as ProfessionalProfile | '',
    oabNumber: '',
    oabState: '',
    termsAccepted: false,
  });
  const [registerLoading, setRegisterLoading] = useState(false);
  const [registerError, setRegisterError] = useState('');
  
  // Terms modal state
  const [termsModalOpen, setTermsModalOpen] = useState(false);
  const [privacyModalOpen, setPrivacyModalOpen] = useState(false);
  const [termsType, setTermsType] = useState<'terms' | 'privacy'>('terms');

  // Ref for auto-scroll to error
  const registerErrorRef = useRef<HTMLDivElement>(null);

  // Deslogar usuário quando acessar a página de login (ex: botão voltar do navegador)
  useEffect(() => {
    if (user) {
      logout();
    }
  }, []); // Executa apenas uma vez quando o componente é montado

  // Scroll to error when it appears in register form
  useEffect(() => {
    if (registerError && registerErrorRef.current) {
      registerErrorRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [registerError]);

  const requiresOAB = [
    ProfessionalProfile.SOCIO,
    ProfessionalProfile.ADVOGADO_ASSOCIADO,
    ProfessionalProfile.ADVOGADO_CELETISTA,
    ProfessionalProfile.ADVOGADO_AUTONOMO,
  ].includes(registerForm.professionalProfile as ProfessionalProfile);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginLoading(true);
    setLoginError('');

    try {
      const response = await api.post('/auth/login', { email, password });
      const { user, accessToken, refreshToken, csrfToken } = response.data.data;

      // Salvar senha se "Lembrar senha" estiver marcado
      if (rememberMe) {
        localStorage.setItem('rememberedEmail', email);
        localStorage.setItem('rememberedPassword', password);
      } else {
        localStorage.removeItem('rememberedEmail');
        localStorage.removeItem('rememberedPassword');
      }

      setAuth(user, accessToken, refreshToken, csrfToken);
      navigate({ to: user?.isClientUser ? '/client' : '/workspaces' });
    } catch (err: any) {
      setLoginError(err.response?.data?.message || 'Erro ao fazer login');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setRegisterLoading(true);
    setRegisterError('');

    // Validações
    if (registerForm.password.length < 8) {
      setRegisterError('A senha deve ter no mínimo 8 caracteres');
      setRegisterLoading(false);
      return;
    }

    if (registerForm.password !== registerForm.confirmPassword) {
      setRegisterError('As senhas não coincidem');
      setRegisterLoading(false);
      return;
    }

    if (!registerForm.termsAccepted) {
      setRegisterError('É necessário aceitar os termos de uso e política de privacidade');
      setRegisterLoading(false);
      return;
    }

    if (requiresOAB && (!registerForm.oabNumber || !registerForm.oabState)) {
      setRegisterError('Número da OAB e UF são obrigatórios para este perfil profissional');
      setRegisterLoading(false);
      return;
    }

    try {
      const response = await api.post('/auth/register', {
        email: registerForm.email,
        password: registerForm.password,
        name: registerForm.fullName.split(' ')[0] || registerForm.fullName, // Primeiro nome
        fullName: registerForm.fullName,
        address: registerForm.address || undefined,
        professionalProfile: registerForm.professionalProfile,
        oabNumber: registerForm.oabNumber || undefined,
        oabState: registerForm.oabState || undefined,
        termsAccepted: registerForm.termsAccepted,
        termsVersion: TERMS_VERSION,
      });

      const { user, accessToken, refreshToken, csrfToken } = response.data.data;
      setAuth(user, accessToken, refreshToken, csrfToken);
      navigate({ to: user?.isClientUser ? '/client' : '/workspaces' });
    } catch (err: any) {
      setRegisterError(err.response?.data?.message || 'Erro ao criar conta');
    } finally {
      setRegisterLoading(false);
    }
  };

  const openTermsModal = (type: 'terms' | 'privacy') => {
    setTermsType(type);
    if (type === 'terms') {
      setTermsModalOpen(true);
    } else {
      setPrivacyModalOpen(true);
    }
  };

  return (
    <>
      <style>{`
        input::placeholder,
        select::placeholder {
          color: #2D3748;
          opacity: 0.6;
        }
        select option {
          color: #2D3748;
        }
      `}</style>
      <div
        className="min-h-screen relative overflow-hidden"
        style={{
          fontFamily: "'Poppins', sans-serif",
        }}
      >
      {/* Background interativo com malha e ondas */}
      <InteractiveBackground />
      
      {/* Container centralizado */}
      <div className="min-h-screen flex items-center justify-center relative z-10 p-8">
        <div
          style={{
            width: '820px',
            height: '550px',
            background: '#FFFFFF',
            borderRadius: '16px',
            display: 'flex',
            padding: '25px',
            gap: '20px',
            boxShadow: '0 0 20px rgba(0, 0, 0, 0.1)',
          }}
        >
        {/* Slideshow Container (Left) */}
        <LoginSlideshow />
        
        {/* Login Form Container (Right) */}
        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            maxWidth: '420px',
            height: '500px',
          }}
        >
        {/* Tabs */}
        <div className="flex mb-5 border-b" style={{ borderColor: '#E2E8F0' }}>
          <button
            onClick={() => setActiveTab('login')}
            className="flex-1 py-2 text-center transition-colors"
            style={{
              fontFamily: "'Poppins', sans-serif",
              fontSize: '12px',
              fontWeight: activeTab === 'login' ? 600 : 400,
              color: '#2D3748',
              borderBottom: activeTab === 'login' ? '2px solid #2D3748' : '2px solid transparent',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = '#2D3748';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = '#2D3748';
            }}
          >
            Login
          </button>
          <button
            onClick={() => setActiveTab('register')}
            className="flex-1 py-2 text-center transition-colors"
            style={{
              fontFamily: "'Poppins', sans-serif",
              fontSize: '12px',
              fontWeight: activeTab === 'register' ? 600 : 400,
              color: '#2D3748',
              borderBottom: activeTab === 'register' ? '2px solid #2D3748' : '2px solid transparent',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = '#2D3748';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = '#2D3748';
            }}
          >
            Criar Conta
          </button>
        </div>

        {/* Login Form */}
        {activeTab === 'login' && (
          <form onSubmit={handleLogin} className="flex flex-col flex-1">
            {loginError && (
              <div
                className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg mb-4"
                style={{
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: '12px',
                }}
              >
                {loginError}
              </div>
            )}
            <div style={{ marginBottom: '20px', marginTop: '20px' }}>
              <label htmlFor="login-email" className="block mb-2" style={{ fontFamily: "'Poppins', sans-serif", fontSize: '12px', color: '#2D3748', fontWeight: 500 }}>
                E-mail
              </label>
              <input
                id="login-email"
                type="email"
                required
                className="w-full px-4 py-2.5 focus:outline-none"
                style={{
                  background: '#F8F8F8',
                  border: 'none',
                  borderRadius: '20px',
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: '12px',
                  boxShadow: '0 0 10px rgba(0, 0, 0, 0.05)',
                  color: '#2D3748',
                }}
                placeholder="seu@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div style={{ marginBottom: '20px' }}>
              <label htmlFor="login-password" className="block mb-2" style={{ fontFamily: "'Poppins', sans-serif", fontSize: '12px', color: '#2D3748', fontWeight: 500 }}>
                Senha
              </label>
              <input
                id="login-password"
                type="password"
                required
                className="w-full px-4 py-2.5 focus:outline-none"
                style={{
                  background: '#F8F8F8',
                  border: 'none',
                  borderRadius: '20px',
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: '12px',
                  boxShadow: '0 0 10px rgba(0, 0, 0, 0.05)',
                  color: '#2D3748',
                }}
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            <div className="flex items-center mb-6">
              <input
                type="checkbox"
                id="rememberMe"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="mr-2"
                style={{ accentColor: '#233444' }}
              />
              <label htmlFor="rememberMe" style={{ fontFamily: "'Poppins', sans-serif", fontSize: '12px', color: '#2D3748' }}>
                Salvar senha
              </label>
            </div>

            <button
              type="submit"
              disabled={loginLoading}
              className="w-full py-2.5 px-4 text-white font-medium transition-colors disabled:opacity-50 mt-auto"
              style={{
                backgroundColor: '#233444',
                borderRadius: '20px',
                fontFamily: "'Poppins', sans-serif",
                fontSize: '12px',
                marginTop: '50px',
                boxShadow: '0 0 10px rgba(0, 0, 0, 0.05)',
              }}
              onMouseEnter={(e) => {
                if (!loginLoading) {
                  e.currentTarget.style.backgroundColor = '#1a2833';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#233444';
              }}
            >
              {loginLoading ? 'Entrando...' : 'Entrar'}
            </button>

            {/* Google Login Button */}
            <div className="mt-4">
              <button
                type="button"
                onClick={async () => {
                  try {
                    // Obter a URL base da API (remover /api/v1 se presente)
                    const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000';
                    const apiBaseUrl = baseUrl.replace('/api/v1', '');
                    // Redirecionar para o endpoint de autenticação Google
                    window.location.href = `${apiBaseUrl}/auth/google`;
                  } catch (error) {
                    console.error('Erro ao iniciar login com Google:', error);
                    alert('Erro ao fazer login com Google. Tente novamente.');
                  }
                }}
                className="w-full py-2.5 px-4 font-medium transition-colors flex items-center justify-center gap-3"
                style={{
                  backgroundColor: '#FFFFFF',
                  border: '1px solid #E2E8F0',
                  borderRadius: '20px',
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: '12px',
                  color: '#2D3748',
                  boxShadow: '0 0 10px rgba(0, 0, 0, 0.05)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#F8F8F8';
                  e.currentTarget.style.borderColor = '#CBD5E0';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#FFFFFF';
                  e.currentTarget.style.borderColor = '#E2E8F0';
                }}
              >
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M17.64 9.20454C17.64 8.56636 17.5827 7.95272 17.4764 7.36363H9V10.845H13.8436C13.635 11.97 13.0009 12.9231 12.0477 13.5613V15.8195H14.9564C16.6582 14.2527 17.64 11.9454 17.64 9.20454Z"
                    fill="#4285F4"
                  />
                  <path
                    d="M9 18C11.43 18 13.467 17.1941 14.9564 15.8195L12.0477 13.5613C11.2418 14.1013 10.2109 14.4204 9 14.4204C6.65454 14.4204 4.67182 12.8372 3.96409 10.71H0.957275V13.0418C2.43818 15.9831 5.48182 18 9 18Z"
                    fill="#34A853"
                  />
                  <path
                    d="M3.96409 10.71C3.78409 10.17 3.68182 9.59318 3.68182 9C3.68182 8.40681 3.78409 7.83 3.96409 7.29V4.95818H0.957273C0.347727 6.17318 0 7.54772 0 9C0 10.4523 0.347727 11.8268 0.957273 13.0418L3.96409 10.71Z"
                    fill="#FBBC05"
                  />
                  <path
                    d="M9 3.57955C10.3214 3.57955 11.5077 4.03364 12.4405 4.92545L15.0218 2.34409C13.4632 0.891818 11.4259 0 9 0C5.48182 0 2.43818 2.01682 0.957275 4.95818L3.96409 7.29C4.67182 5.16273 6.65454 3.57955 9 3.57955Z"
                    fill="#EA4335"
                  />
                </svg>
                Continuar com Google
              </button>
            </div>

            {/* Recuperar senha link */}
            <div className="text-center mt-4">
              <button
                type="button"
                onClick={() => {
                  // TODO: Implementar recuperação de senha
                  alert('Funcionalidade de recuperação de senha em breve');
                }}
                style={{
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: '12px',
                  color: '#2D3748',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.opacity = '0.8';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = '1';
                }}
              >
                Recuperar senha
              </button>
            </div>
          </form>
        )}

        {/* Register Form */}
        {activeTab === 'register' && (
          <form onSubmit={handleRegister} className="space-y-3 flex-1 overflow-y-auto pr-2" style={{ maxHeight: '450px' }}>
            <div>
              <label className="block mb-1" style={{ fontFamily: "'Poppins', sans-serif", fontSize: '12px', color: '#2D3748', fontWeight: 500 }}>
                Nome Completo <span style={{ color: '#EF4444' }}>*</span>
              </label>
              <input
                type="text"
                required
                className="w-full px-4 py-2.5 focus:outline-none"
                style={{
                  background: '#F8F8F8',
                  border: 'none',
                  borderRadius: '20px',
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: '12px',
                  boxShadow: '0 0 10px rgba(0, 0, 0, 0.05)',
                  color: '#2D3748',
                }}
                placeholder="João Silva"
                value={registerForm.fullName}
                onChange={(e) => setRegisterForm({ ...registerForm, fullName: e.target.value })}
              />
            </div>

            <div>
              <label className="block mb-1" style={{ fontFamily: "'Poppins', sans-serif", fontSize: '12px', color: '#2D3748', fontWeight: 500 }}>
                E-mail <span style={{ color: '#EF4444' }}>*</span>
              </label>
              <input
                type="email"
                required
                className="w-full px-4 py-2.5 focus:outline-none"
                style={{
                  background: '#F8F8F8',
                  border: 'none',
                  borderRadius: '20px',
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: '12px',
                  boxShadow: '0 0 10px rgba(0, 0, 0, 0.05)',
                  color: '#2D3748',
                }}
                placeholder="seu@email.com"
                value={registerForm.email}
                onChange={(e) => setRegisterForm({ ...registerForm, email: e.target.value })}
              />
            </div>

            <div>
              <label className="block mb-1" style={{ fontFamily: "'Poppins', sans-serif", fontSize: '12px', color: '#2D3748', fontWeight: 500 }}>
                Senha <span style={{ color: '#EF4444' }}>*</span>
              </label>
              <input
                type="password"
                required
                className="w-full px-4 py-2.5 focus:outline-none"
                style={{
                  background: '#F8F8F8',
                  border: 'none',
                  borderRadius: '20px',
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: '12px',
                  boxShadow: '0 0 10px rgba(0, 0, 0, 0.05)',
                  color: '#2D3748',
                }}
                placeholder="Mínimo 8 caracteres"
                value={registerForm.password}
                onChange={(e) => setRegisterForm({ ...registerForm, password: e.target.value })}
              />
            </div>

            <div>
              <label className="block mb-1" style={{ fontFamily: "'Poppins', sans-serif", fontSize: '12px', color: '#2D3748', fontWeight: 500 }}>
                Confirmar Senha <span style={{ color: '#EF4444' }}>*</span>
              </label>
              <input
                type="password"
                required
                className="w-full px-4 py-2.5 focus:outline-none"
                style={{
                  background: '#F8F8F8',
                  border: 'none',
                  borderRadius: '20px',
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: '12px',
                  boxShadow: '0 0 10px rgba(0, 0, 0, 0.05)',
                  color: '#2D3748',
                }}
                placeholder="Digite a senha novamente"
                value={registerForm.confirmPassword}
                onChange={(e) => setRegisterForm({ ...registerForm, confirmPassword: e.target.value })}
              />
            </div>

            <div>
              <label className="block mb-1" style={{ fontFamily: "'Poppins', sans-serif", fontSize: '12px', color: '#2D3748', fontWeight: 500 }}>
                Endereço
              </label>
              <input
                type="text"
                className="w-full px-4 py-2.5 focus:outline-none"
                style={{
                  background: '#F8F8F8',
                  border: 'none',
                  borderRadius: '20px',
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: '12px',
                  boxShadow: '0 0 10px rgba(0, 0, 0, 0.05)',
                  color: '#2D3748',
                }}
                placeholder="Rua, número, cidade, estado"
                value={registerForm.address}
                onChange={(e) => setRegisterForm({ ...registerForm, address: e.target.value })}
              />
            </div>

            <div>
              <label className="block mb-1" style={{ fontFamily: "'Poppins', sans-serif", fontSize: '12px', color: '#2D3748', fontWeight: 500 }}>
                Perfil <span style={{ color: '#EF4444' }}>*</span>
              </label>
              <select
                required
                className="w-full px-4 py-2.5 focus:outline-none"
                style={{
                  background: '#F8F8F8',
                  border: 'none',
                  borderRadius: '20px',
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: '12px',
                  boxShadow: '0 0 10px rgba(0, 0, 0, 0.05)',
                  color: '#2D3748',
                }}
                value={registerForm.professionalProfile}
                onChange={(e) => setRegisterForm({ ...registerForm, professionalProfile: e.target.value as ProfessionalProfile })}
              >
                <option value="">Selecione um perfil</option>
                {PROFESSIONAL_PROFILES.map((profile) => (
                  <option key={profile.value} value={profile.value}>
                    {profile.label}
                  </option>
                ))}
              </select>
            </div>

            {requiresOAB && (
              <>
                <div>
                  <label className="block mb-1" style={{ fontFamily: "'Poppins', sans-serif", fontSize: '12px', color: '#2D3748', fontWeight: 500 }}>
                    Número da OAB <span style={{ color: '#EF4444' }}>*</span>
                  </label>
                  <input
                    type="text"
                    required={requiresOAB}
                    className="w-full px-4 py-2.5 focus:outline-none"
                    style={{
                      background: '#F8F8F8',
                      border: 'none',
                      borderRadius: '20px',
                      fontFamily: "'Poppins', sans-serif",
                      fontSize: '12px',
                      boxShadow: '0 0 10px rgba(0, 0, 0, 0.05)',
                      color: '#2D3748',
                    }}
                    placeholder="123456"
                    value={registerForm.oabNumber}
                    onChange={(e) => setRegisterForm({ ...registerForm, oabNumber: e.target.value })}
                  />
                </div>

                <div>
                  <label className="block mb-1" style={{ fontFamily: "'Poppins', sans-serif", fontSize: '12px', color: '#2D3748', fontWeight: 500 }}>
                    UF da OAB <span style={{ color: '#EF4444' }}>*</span>
                  </label>
                  <select
                    required={requiresOAB}
                    className="w-full px-4 py-2.5 focus:outline-none"
                    style={{
                      background: '#F8F8F8',
                      border: 'none',
                      borderRadius: '20px',
                      fontFamily: "'Poppins', sans-serif",
                      fontSize: '12px',
                      boxShadow: '0 0 10px rgba(0, 0, 0, 0.05)',
                      color: '#2D3748',
                    }}
                    value={registerForm.oabState}
                    onChange={(e) => setRegisterForm({ ...registerForm, oabState: e.target.value })}
                  >
                    <option value="">Selecione a UF</option>
                    {BRAZILIAN_STATES.map((state) => (
                      <option key={state} value={state}>
                        {state}
                      </option>
                    ))}
                  </select>
                </div>
              </>
            )}

            <div className="flex items-start">
              <input
                type="checkbox"
                id="terms"
                checked={registerForm.termsAccepted}
                onChange={(e) => setRegisterForm({ ...registerForm, termsAccepted: e.target.checked })}
                className="mt-1 mr-2"
                style={{ accentColor: '#233444' }}
              />
              <label htmlFor="terms" style={{ fontFamily: "'Poppins', sans-serif", fontSize: '12px', color: '#2D3748' }}>
                Li e aceito os{' '}
                <button
                  type="button"
                  onClick={() => openTermsModal('terms')}
                  className="text-indigo-600 hover:underline"
                  style={{ fontFamily: "'Poppins', sans-serif" }}
                >
                  Termos de Uso
                </button>
                {' '}e{' '}
                <button
                  type="button"
                  onClick={() => openTermsModal('privacy')}
                  className="text-indigo-600 hover:underline"
                  style={{ fontFamily: "'Poppins', sans-serif" }}
                >
                  Política de Privacidade
                </button>
                <span style={{ color: '#EF4444' }}> *</span>
              </label>
            </div>

            {registerError && (
              <div
                ref={registerErrorRef}
                className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg"
                style={{
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: '12px',
                }}
              >
                {registerError}
              </div>
            )}

            <button
              type="submit"
              disabled={registerLoading}
              className="w-full py-2 px-4 text-white font-medium transition-colors disabled:opacity-50"
              style={{
                backgroundColor: '#233444',
                borderRadius: '20px',
                fontFamily: "'Poppins', sans-serif",
                fontSize: '12px',
              }}
              onMouseEnter={(e) => {
                if (!registerLoading) e.currentTarget.style.backgroundColor = '#1a2833';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#233444';
              }}
            >
              {registerLoading ? 'Criando conta...' : 'Criar Conta'}
            </button>
          </form>
        )}
        </div>
        </div>
      </div>

      {/* Terms Modals */}
      <TermsModal
        isOpen={termsModalOpen}
        onClose={() => setTermsModalOpen(false)}
        type="terms"
      />
      <TermsModal
        isOpen={privacyModalOpen}
        onClose={() => setPrivacyModalOpen(false)}
        type="privacy"
      />

      {/* Logo no rodapé */}
      <div
        style={{
          position: 'fixed',
          bottom: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 10,
        }}
      >
        <img
          src="/logo-login.svg"
          alt="Prometeus"
          style={{ width: '24px', height: '24px' }}
        />
      </div>

      {/* Versão da aplicação no canto inferior direito */}
      <div
        style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          zIndex: 10,
          fontFamily: "'Poppins', sans-serif",
          fontSize: '11px',
          color: '#718096',
          opacity: 0.7,
        }}
      >
        v{APP_VERSION}
      </div>
    </div>
    </>
  );
}
